import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VendorProductionTime } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Info, Zap } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface ProductionTimeByVendorTableProps {
  data: VendorProductionTime[];
  isLoading: boolean;
}

type SortField = 'jobCount' | 'avgProductionTime' | 'medianProductionTime' | 'fastestJob' | 'slowestJob';
type SortDirection = 'asc' | 'desc';

const ROWS_PER_PAGE = 15;

export function ProductionTimeByVendorTable({ data, isLoading }: ProductionTimeByVendorTableProps) {
  const [sortField, setSortField] = useState<SortField>('jobCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(0);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const sortedData = [...data].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return (a[sortField] - b[sortField]) * multiplier;
  });

  const totalPages = Math.ceil(sortedData.length / ROWS_PER_PAGE);
  const paginatedData = sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const SortableHeader = ({ field, label, align = 'left' }: { field: SortField; label: string; align?: 'left' | 'right' }) => (
    <button
      onClick={() => handleSort(field)}
      className={cn(
        "flex items-center gap-1 hover:text-foreground transition-colors w-full",
        sortField === field && "text-foreground",
        align === 'right' && "justify-end"
      )}
    >
      {label}
      {sortField === field && (
        sortDirection === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
      )}
    </button>
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Production Time by Vendor</CardTitle>
          <CardDescription>Vendor delivery speed comparison</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Production Time by Vendor</CardTitle>
          <CardDescription>Vendor delivery speed comparison</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No vendors with 2+ completed jobs in the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Production Time by Vendor
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Production Time = Time from Work In Progress to Job Completion Submitted (in business days)</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <CardDescription>Vendor delivery speed comparison (min. 2 completed jobs)</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Vendor</TableHead>
                  <TableHead className="text-right">
                    <SortableHeader field="jobCount" label="Jobs" align="right" />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeader field="avgProductionTime" label="Avg Time" align="right" />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeader field="medianProductionTime" label="Median" align="right" />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeader field="fastestJob" label="Fastest" align="right" />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeader field="slowestJob" label="Slowest" align="right" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map(vendor => (
                  <TableRow key={vendor.vendorId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{vendor.vendorName}</span>
                        {vendor.medianProductionTime < 5 && (
                          <Badge variant="secondary" className="gap-1 text-xs">
                            <Zap className="h-3 w-3" />
                            Fast
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{vendor.jobCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{vendor.avgProductionTime.toFixed(1)} days</TableCell>
                    <TableCell className="text-right tabular-nums">{vendor.medianProductionTime.toFixed(1)} days</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                      {vendor.fastestJob.toFixed(1)} days
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {vendor.slowestJob.toFixed(1)} days
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {page * ROWS_PER_PAGE + 1}-{Math.min((page + 1) * ROWS_PER_PAGE, sortedData.length)} of {sortedData.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
